import { Injectable } from "@angular/core";
import { ProductId } from "@bca-shared/models/_types";
import { IProduct } from "@bca-shared/models/product";
import {
  AddShippingInfoProperties,
  AddToCartProperties,
  BeginCheckoutProperties,
  Currency,
  Items,
  NsShop,
  RemoveFromCartProperties,
  ViewItemListProperties,
  ViewItemProperties,
} from "./Avo";
import { SalesChanelType } from "./avo-event-details.models";

@Injectable({
  providedIn: "root",
})
export class AvoDataTransformService {
  // value is 0 for no item is added, removed or purchased yet
  VIEW_ITEM_VALUE = 0;
  SALES_CHANNEL = "Business Card aanvragen";
  CLASS_ADDON = "BFA";
  ITEM_BRAND = "business_card";

  public transformBeginCheckoutAction(
    products: IProduct[],
  ): BeginCheckoutProperties {
    return {
      currency: "EUR",
      coupon: "",
      value: this.getCartValue(products),
      items: this.transformItems(products),
      nsShop: NsShop.NS_BUSINESSCARD,
      salesChannel: SalesChanelType.BCA,
    };
  }

  public transformViewItemAction(product: IProduct): ViewItemProperties {
    return {
      value: this.transformCentsToEuros(product.price),
      items: this.transformItems([product]),
      nsShop: NsShop.NS_BUSINESSCARD,
      salesChannel: SalesChanelType.BCA,
    };
  }

  public transformViewItemListAction(
    products: IProduct[],
  ): ViewItemListProperties {
    return {
      value: this.getCartValue(products),
      items: this.transformItems(products),
      itemListId: "BCA",
      itemListName: "NS-Business card",
      nsShop: NsShop.NS_BUSINESSCARD,
      salesChannel: SalesChanelType.BCA,
    };
  }

  public addShippingInfo(products: IProduct[]): AddShippingInfoProperties {
    return {
      currency: Currency.EUR,
      value: this.getCartValue(products),
      items: this.transformItems(products),
      nsShop: NsShop.NS_BUSINESSCARD,
      salesChannel: SalesChanelType.BCA,
    };
  }

  public addPaymentInfo(products: IProduct[]): AddShippingInfoProperties {
    return {
      currency: Currency.EUR,
      value: this.getCartValue(products),
      items: this.transformItems(products),
      nsShop: NsShop.NS_BUSINESSCARD,
      salesChannel: SalesChanelType.BCA,
    };
  }

  public transformCartAction(products: IProduct[]): AddToCartProperties {
    return {
      currency: "EUR",
      value: this.getCartValue(products),
      items: this.transformItems(products),
      nsShop: NsShop.NS_BUSINESSCARD,
    };
  }

  public transformRemoveFromCartAction(product: IProduct): RemoveFromCartProperties {
    return {
      currency: "EUR",
      value: this.transformCentsToEuros(product.price),
      items: this.transformItems([product]),
      nsShop: NsShop.NS_BUSINESSCARD,
        websiteSection:"ns-zakelijk|checkout|samenstellen",
        existingProduct: [{
          itemId: product.identifier,
          itemName: product.identifier,
          itemBrand: "BCA",
          itemCategory: "Abonnementen",
          itemCategory2: "Abonnementen",
          itemVariant: ["BCA"],
          price: product.price,
          quantity: 1
        }]
    };
  }

  public transformItems(products: IProduct[]): Items[] {
    return products.map((product, index) =>
      this.transformProductToItem(product, index),
    );
  }
  public transformItem(product: IProduct): Items {
    return this.transformProductToItem(product);
  }

  private transformProductToItem(product: IProduct, index?: number): Items {
    const clazz = product.addons.find(
      (addon) => addon.identifier === this.CLASS_ADDON,
    )?.clazz;

    const intercityDirect = product.addons.find((addon) =>
      addon.identifier?.includes("update"),
    )
      ? true
      : false;

    return {
      itemId: product.identifier ?? "",
      itemName: this.retrieveProductName(product.identifier),
      itemRevenue: this.transformCentsToEuros(product.price),
      coupon: null,
      discount: null,
      index: index,
      quantity: 1,
      price: this.transformCentsToEuros(product.price),
      itemBrand: this.ITEM_BRAND,
      itemVariant: null,
      itemCategory: product.hasSticker ? "Stickered" : "Non-Stickered",
      travelDate: null,
      intercityDirect: intercityDirect,
      affiliation: product.user?.department ?? null,
      itemCategory2: null,
      deurTotDeur: null,
      destinationStation: product.route?.to?.stationscode ?? null,
      originStation: product.route?.from?.stationscode ?? null,
      travelClass: clazz ?? null,
      travelRoute: product.route
        ? `${product.route.from.stationscode} - ${product.route.to.stationscode}`
        : null,
    };
  }

  private getCartValue(products: IProduct[]): number {
    const cartValue = products.reduce((total, product) => {
      return total + (product.totalPrice ?? product.price);
    }, 0);
    return this.transformCentsToEuros(cartValue);
  }

  private transformCentsToEuros(price: number): number {
    if (isNaN(price) || price < 0) {
      throw new Error("Invalid price value");
    }
    return parseFloat((price / 100).toFixed(2));
  }

  private retrieveProductName(id: ProductId) {
    switch (id) {
      case "BCB":
        return "Zakelijk Geen abonnement";
      case "BCD":
        return "Zakelijk Dal abonnement";
      case "BCT":
        return "Zakelijk Traject abonnement";
      case "BCS":
        return "Zakelijk Trein Vrij abonnement";
      case "VRIJ":
        return "Zakelijk OV Vrij abonnement";

      default:
        break;
    }
  }
}
